import { useParams, useNavigate } from "react-router-dom";
import type { Platform } from "../../../shared/types.ts";
import { useChatThread, useSendMessage, useFinalizeThread } from "../hooks/useChat.ts";
import { useContentRun } from "../hooks/useContent.ts";
import { ChatThread } from "../components/chat/ChatThread.tsx";
import { ChatInput } from "../components/chat/ChatInput.tsx";
import { Button } from "../components/ui/Button.tsx";
import { Badge } from "../components/ui/Badge.tsx";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader.tsx";

export function Chat() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();

  const { data: threadData, isLoading, error } = useChatThread(threadId);
  const sendMessage = useSendMessage(threadId || "");
  const finalizeThread = useFinalizeThread(threadId || "");

  // Extract thread info
  const thread = threadData?.data;
  const messages = thread?.messages ?? [];

  // Fetch the associated content run for idea context
  const { data: runData } = useContentRun(thread?.run_id ?? undefined);
  const run = runData?.data;
  const ideaIdx = thread?.idea_index;
  const selectedIdea =
    thread &&
    run?.ideas &&
    ideaIdx != null &&
    ideaIdx >= 0 &&
    ideaIdx < run.ideas.length
      ? run.ideas[ideaIdx]
      : null;

  // Determine if we're waiting for AI response
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isWaitingForAI =
    lastMessage?.role === "user" || sendMessage.isPending;

  const handleSend = (content: string) => {
    if (!threadId) return;
    sendMessage.mutate({ content });
  };

  const handleFinalize = async () => {
    if (!threadId) return;
    try {
      await finalizeThread.mutateAsync();
      if (thread?.run_id) {
        navigate(`/review/${thread.run_id}`);
      }
    } catch {
      // Error is available via finalizeThread.error
    }
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  // ── Error state ──
  if (error || !thread) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          {error ? "Failed to load chat thread." : "Chat thread not found."}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/home")}
        >
          Back to workspace
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-4">
      <div className="shrink-0 px-4 pt-4">
        <WorkspaceHeader
          eyebrow="Operator Workspace"
          title={selectedIdea ? selectedIdea.headline : "Brainstorm"}
          description={selectedIdea ? selectedIdea.angle : "Refine the selected idea with focused back-and-forth before finalizing."}
          breadcrumbs={[
            { label: "Workspace", href: "/home" },
            { label: "Chat" },
          ]}
          badges={selectedIdea?.target_platforms?.length ? (
            <div className="flex flex-wrap gap-1">
              {selectedIdea.target_platforms.map((platform: Platform) => (
                <Badge key={platform} variant="default">
                  {platform}
                </Badge>
              ))}
            </div>
          ) : undefined}
          actions={(
            <Button
              variant="primary"
              size="sm"
              loading={finalizeThread.isPending}
              onClick={handleFinalize}
            >
              Finalize
            </Button>
          )}
          metadata={finalizeThread.isError ? (
            <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-600 ring-1 ring-red-200">
              Failed to finalize. Please try again.
            </span>
          ) : (
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Thread refinement / finalization
            </span>
          )}
        />
      </div>

      <ChatThread messages={messages} isWaiting={isWaitingForAI} />

      <ChatInput
        onSend={handleSend}
        disabled={sendMessage.isPending}
      />
    </div>
  );
}
